import { useAppDispatch, useAppSelector, useHandlePageNavigate } from "./hooks";
import {
  getReportDifferenceAsync,
  pullPeopleReportAsync,
  searchPeopleAsync,
  selectPeopleSearchStatus,
  selectReportStatus,
  selectAllReportNotesList,
} from "../features/reports/reportsSlice";
import { useLocation } from "react-router-dom";
import { PeopleReport } from "../features/reports/reportTypes";
import { selectMonitoring } from "../features/monitoring/monitoringSlice";
import { useGetSpecificReportNotesDispatch } from "./notesHook";
import {
  setPeopleSearch,
  toggleReinitialize,
} from "../features/multisearch/multisearchSlice";
import { getSpecificReportNotesParamBuilder } from "../utils";
import { sendMemberEventAsync } from "../features/admin/adminSlice";
import { setDisplayOnReport } from "../features/profileProtection/profileProtectionSlice";

export type PullPeopleReportParams = string;

type UsePeopleReportHook = () => (
  token: PullPeopleReportParams,
  pullLocation: string,
  optOut?: boolean,
) => void;

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/* Pull People Report */
const usePeopleReportDispatch: UsePeopleReportHook = () => {
  const dispatch = useAppDispatch();
  const reportStatus = useAppSelector(selectReportStatus);
  const monitoredReports = useAppSelector(selectMonitoring);
  const query = useQuery();

  const allReportNotesList = useAppSelector(selectAllReportNotesList);
  const getSpecificReportNotes = useGetSpecificReportNotesDispatch();

  const version = query.get("version");

  const pageNavigate = useHandlePageNavigate();
  const location = useLocation();

  const pullPeopleReport = (
    token: PullPeopleReportParams,
    pullLocation: string,
    optOut: boolean = false,
  ) => {
    if (reportStatus === "loading") return;
    if (location.pathname !== "/people-report") {
      pageNavigate("/people-report");
    } else {
      window.scrollTo(0, 0);
    }
    dispatch(pullPeopleReportAsync({ reportToken: token }))
      .unwrap()
      .then(result => {
        if (result.success) {
          const report = result.report.result as PeopleReport;
          const monitoredReport = monitoredReports.find(
            report => report.reportToken === token,
          );

          if (version) {
            dispatch(
              getReportDifferenceAsync({
                reportToken: token,
                versionA: report.reportSummary.version!,
                versionB: version,
              }),
            );
          }

          if (
            !version &&
            monitoredReport &&
            monitoredReport.versionZ !== report.reportSummary.version &&
            monitoredReport.versionZ &&
            report.reportSummary.version
          ) {
            dispatch(
              getReportDifferenceAsync({
                reportToken: token,
                versionA: report.reportSummary.version!,
                versionB: monitoredReport.versionZ!,
              }),
            );
          }
          let peopleReportParams = report
            ? getSpecificReportNotesParamBuilder(report)
            : undefined;
          if (
            allReportNotesList?.some(
              notes =>
                notes && notes.reportType === "people" && notes.token === token,
            ) &&
            report &&
            peopleReportParams
          ) {
            getSpecificReportNotes(peopleReportParams);
          }

          dispatch(
            sendMemberEventAsync({
              EventAction: "Search",
              EventCategory: "People",
              MetaData: JSON.stringify({ "pull location": pullLocation }),
            }),
          );
          if (optOut) {
            dispatch(setDisplayOnReport(true));
          }
        }
      })
      .catch(error => {
        console.log(error.message);
        pageNavigate("/");
      });
  };

  return pullPeopleReport;
};

/* People Search */
export type SearchPeopleParams = {
  firstName: string;
  lastName: string;
  state: string;
  city: string;
  minAge: number | string;
  maxAge: number | string;
  optOut?: boolean | null;
};

type UsePeopleSearchHook = () => (params: SearchPeopleParams) => void;

const usePeopleSearchDispatch: UsePeopleSearchHook = () => {
  const dispatch = useAppDispatch();
  const searchStatus = useAppSelector(selectPeopleSearchStatus);
  const pageNavigate = useHandlePageNavigate();
  const location = useLocation();

  const searchPeople = async (params: SearchPeopleParams) => {
    if (searchStatus === "loading") return;

    dispatch(searchPeopleAsync(params));
    dispatch(setPeopleSearch(params));
    dispatch(toggleReinitialize());

    if (params.optOut && location.pathname !== "/protection/search") {
      pageNavigate("/protection/search");
      return;
    } else if (params.optOut && location.pathname === "/protection/search") {
      window.scrollTo(0, 0);
      return;
    }

    if (location.pathname !== "/people-search") {
      pageNavigate("/people-search");
    } else {
      window.scrollTo(0, 0);
    }
  };

  return searchPeople;
};

export { usePeopleReportDispatch, usePeopleSearchDispatch };
